/**
 * Holdings Tab (Asset scope only)
 * 
 * Shows all positions aggregated into this asset
 * Click row to switch to Position scope
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CurrencyAmount } from '@/components/ui/CurrencyAmount';
import { ASSET_CLASS_ICONS } from '@/constants/investmentIcons';

interface HoldingsTabProps {
  data: any;
  onPositionClick: (accountId: string) => void;
}

export function HoldingsTab({ data, onPositionClick }: HoldingsTabProps) {
  if (!data.positions || data.positions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No positions found
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Holdings ({data.positions.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg text-xs">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="text-left text-xs font-medium text-muted-foreground px-3">Security</TableHead>
                <TableHead className="text-left text-xs font-medium text-muted-foreground w-20 px-3">Currency</TableHead>
                <TableHead className="text-right text-xs font-medium text-muted-foreground w-28 px-3">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.positions.map((position: any) => {
                const Icon = ASSET_CLASS_ICONS[position.asset_type as keyof typeof ASSET_CLASS_ICONS] || ASSET_CLASS_ICONS['unclassified'];
                return (
                  <TableRow
                    key={position.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onPositionClick(position)}
                  >
                    <TableCell className="px-3">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <div className="min-w-0 max-w-[200px] sm:max-w-[240px]">
                          <div className="truncate font-medium text-sm">{position.asset_name || position.account_name}</div>
                          {position.symbol && (
                            <div className="text-xs text-muted-foreground truncate">{position.symbol}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs w-20 px-3">{(position.currency || 'CAD').toUpperCase()}</TableCell>
                    <TableCell className="text-right w-28 px-3">
                      <CurrencyAmount
                        value={position.market_value}
                        currency={position.currency || 'CAD'}
                        showCurrency={true}
                        className="font-medium text-xs"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
